'use client';

import { type MouseEvent, useState } from 'react';

import { Caption } from '@depop/web-thrift-ui/Typography';
import { CancelIcon } from '@depop/web-ui-kit/Icons/CancelIcon';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { ButtonMinimal } from '@/components/ButtonMinimal';
import { ExtendedLink } from '@/components/ExtendedLink';
import { ActivityTrackerEventType } from '@/modules/activityTracker/constants';
import { navigateAfterTracking } from '@/modules/activityTracker/navigateAfterTracking';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { useCurrentRoute } from '@/modules/activityTracker/useCurrentRoute';
import { fallbackLinks } from '@/modules/appStoreLinks/constants';
import { DeviceType } from '@/modules/device/types';
import { useUserAgent } from '@/modules/device/useUserAgent';
import { useBranchDeepLink } from '@/modules/externalTracking/useBranchDeepLink';
import { useCurrentLanguage } from '@/modules/language/useCurrentLanguage';
import {
  getSessionStorageItem,
  setSessionStorageItem,
} from '@/modules/storage';
import { useCurrentUser } from '@/modules/user/useCurrentUser';
import {
  VISUAL_SEARCH_BANNER_DISMISSED_KEY,
  VISUAL_SEARCH_BANNER_IMAGE,
  VISUAL_SEARCH_BANNER_TRACKING_ID,
} from '@/modules/visualSearch/constants';

export function VisualSearchPromoBanner() {
  const [isDismissed, setIsDismissed] = useState(
    () => getSessionStorageItem(VISUAL_SEARCH_BANNER_DISMISSED_KEY) === 'true'
  );
  const t = useTranslations('common');
  const { language } = useCurrentLanguage();
  const { device } = useUserAgent();
  const [, user] = useCurrentUser();
  const { sendActivityTrackerEvent } = useActivityTracker({ userId: user?.id });
  const urlPath = usePathname();
  const page = useCurrentRoute();

  const isAndroid = device === DeviceType.ANDROID;
  const appStoreLink = useBranchDeepLink({
    fallback: fallbackLinks[language][isAndroid ? 'PlayStore' : 'AppStore'],
    appPath: '/',
    referer: '',
    stage: 'download app link',
    feature: 'organic',
    channel: 'website',
    campaign: '',
  });

  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    navigateAfterTracking(
      event,
      sendActivityTrackerEvent(
        ActivityTrackerEventType.APP_DOWNLOAD_ACTION,
        {
          page,
          urlPath,
          ctaId: VISUAL_SEARCH_BANNER_TRACKING_ID,
          schemaVersion: '1.0',
        },
        { waitForSend: true }
      )
    );
  }

  function handleDismiss() {
    setSessionStorageItem(VISUAL_SEARCH_BANNER_DISMISSED_KEY, 'true');
    setIsDismissed(true);
  }

  if (isDismissed) {
    return null;
  }

  return (
    <div className={styles.banner}>
      <ExtendedLink
        href={appStoreLink}
        onClick={handleClick}
        className={styles.link}
      >
        <img className={styles.image} src={VISUAL_SEARCH_BANNER_IMAGE} alt="" />
        <span className={styles.content}>
          <Caption variant="lg-bold" colour="base" as="span">
            {t('VisualSearch.Title')}
          </Caption>
          <Caption variant="lg-regular" colour="base" as="span">
            {t('VisualSearch.Banner.Body')}
          </Caption>
          <Caption variant="lg-bold" colour="base" as="span">
            {t('DownloadTheApp')}
          </Caption>
        </span>
      </ExtendedLink>
      <ButtonMinimal
        className={styles.dismissButton}
        onClick={handleDismiss}
        type="button"
        aria-label={t('Close')}
        title={t('Close')}
      >
        <CancelIcon size={16} aria-hidden="true" />
      </ButtonMinimal>
    </div>
  );
}
